
/**
 * Module depenencies.
 */

import { color, media, units } from 'src/styles/utils';
import { colors } from 'src/styles/colors';
import { getModuleTheme } from 'src/core/utils/colors';
import { normalizeNewLine } from 'src/core/utils/strings';
import { prop } from 'styled-tools';
import Container from 'src/components/core/layout/container';
import Image from 'src/components/core/image';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Type from 'src/components/core/typography';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string,
  className?: string,
  hasPageTitle?: boolean,
  imageUrl: string,
  isKiosk?: boolean,
  lead?: string | null,
  title: string
};

/**
 * Colors theme config.
 */

const colorsThemeConfig = {
  dark: {
    textColor: colors.grey400,
    titleColor: colors.black
  },
  light: {
    textColor: colors.white,
    titleColor: colors.white
  }
};

/**
 * `Section` component.
 */

const Section = styled.section<{ bgColor: string }>`
  background-color: ${prop('bgColor', color('beige200'))};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title . .     .'
      '. lead  . image .';
    grid-template-columns: 1fr 4fr 1fr 5fr 1fr;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-area: title;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H4)<{ titleColor: string }>`
  color: ${prop('titleColor')};
  grid-area: title;
  margin-bottom: ${units(5)};
`;

/**
 * `LeadWrapper` styled component.
 */

const LeadWrapper = styled.div`
  grid-area: lead;
`;

/**
 * `Lead` styled component.
 */

const Lead = styled(Type.H6).attrs({
  as: 'p',
  small: true
})<{ textColor: string }>`
  color: ${prop('textColor')};

  ${media.max('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  min-height: ${units(10)};
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  ${media.min('sm')`
    min-height: ${units(16)};
  `}
`;

/**
 * `AwardsSection` component.
 */

const AwardsSection = (props: Props): ReactElement => {
  const { bgColor, hasPageTitle, imageUrl, isKiosk, lead, title } = props;
  const moduleTheme = getModuleTheme(bgColor) ?? 'dark';
  const { textColor, titleColor } = colorsThemeConfig[moduleTheme];

  return (
    <Section bgColor={bgColor}>
      <Container hasPadding={!isKiosk}>
        <Grid>
          <TitleWrapper>
            <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
              <Title
                as={hasPageTitle ? 'h1' : 'h2'}
                titleColor={titleColor}
              >
                {title}
              </Title>
            </ParallaxFadeInUp>
          </TitleWrapper>

          {lead && (
            <LeadWrapper>
              <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                <Lead textColor={textColor}>
                  <RawHtml>
                    {normalizeNewLine(lead)}
                  </RawHtml>
                </Lead>
              </ParallaxFadeInUp>
            </LeadWrapper>
          )}

          <ImageWrapper>
            <ParallaxFadeInUp parallaxOptions={{ speed: 3 }}>
              <Image
                alt={title}
                layout={'fill'}
                objectFit={'contain'}
                src={imageUrl}
              />
            </ParallaxFadeInUp>
          </ImageWrapper>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `AwardsSection` component.
 */

export default AwardsSection;
